// Arranque y avance de una revisión contra Zentral, real o en modo prueba; el avance se consulta cada 3 s.
import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ReconciliationEntityEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationEntityEnum';
import {
    getOrgReconciliation,
    startOrgReconciliation,
    type GetOrgReconciliationResponse,
} from '@/api/services/retail-org/reconciliation.client';
import {
    getCatalogReconciliation,
    startCatalogReconciliation,
    type GetCatalogReconciliationResponse,
} from '@/api/services/retail-catalog/reconciliation.client';
import { ReconciliationRunStatusEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationRunStatusEnum';
import {
    pollIntervalOf,
    shouldRetryPoll,
    startFailureOf,
    type StartFailure,
} from '../lib/reconciliation';

/** Qué entidad se revisa; inventario puede ir acotado a una tienda. */
export type ReconcileTarget =
    | { entity: ReconciliationEntityEnum.STORES | ReconciliationEntityEnum.USERS }
    | { entity: ReconciliationEntityEnum.CATALOG }
    | { entity: ReconciliationEntityEnum.INVENTORY; storeId?: string };

export type ReconciliationRun = GetOrgReconciliationResponse | GetCatalogReconciliationResponse;

const KEY = ['retail-admin', 'reconciliation'] as const;

function startRun(
    retailerId: string,
    target: ReconcileTarget,
    dryRun: boolean,
): Promise<{ runId: string }> {
    const flag = dryRun ? { dryRun } : {};
    switch (target.entity) {
        case ReconciliationEntityEnum.STORES:
        case ReconciliationEntityEnum.USERS:
            return startOrgReconciliation(retailerId, target.entity, dryRun);
        case ReconciliationEntityEnum.CATALOG:
            return startCatalogReconciliation(retailerId, { entity: target.entity, ...flag });
        case ReconciliationEntityEnum.INVENTORY:
            return startCatalogReconciliation(retailerId, {
                entity: target.entity,
                ...(target.storeId ? { storeId: target.storeId } : {}),
                ...flag,
            });
    }
}

function getRun(
    retailerId: string,
    entity: ReconciliationEntityEnum,
    runId: string,
): Promise<ReconciliationRun> {
    const isOrg =
        entity === ReconciliationEntityEnum.STORES || entity === ReconciliationEntityEnum.USERS;
    return isOrg
        ? getOrgReconciliation(retailerId, runId)
        : getCatalogReconciliation(retailerId, runId);
}

export interface ReconciliationRunState {
    /** Abre la corrida; no hace nada si ya se está abriendo o ya hay una. */
    start: () => void;
    /** Tras un fallo: vuelve a consultar la misma corrida si falló el avance; si no, abre otra. */
    retry: () => void;
    isStarting: boolean;
    failure: StartFailure | null;
    runId: string | null;
    run: ReconciliationRun | undefined;
    pollFailed: boolean;
}

/** Una corrida: el POST que la abre y la consulta de su avance hasta que deja de estar RUNNING. */
export function useReconciliationRun(
    retailerId: string,
    target: ReconcileTarget,
    dryRun: boolean,
): ReconciliationRunState {
    const [runId, setRunId] = useState<string | null>(null);
    const [failure, setFailure] = useState<StartFailure | null>(null);
    // Candado síncrono: dos clics en el mismo render no abren dos corridas.
    const inFlight = useRef(false);

    const start = useMutation({
        mutationFn: () => startRun(retailerId, target, dryRun),
        onSettled: () => {
            inFlight.current = false;
        },
        onSuccess: (result) => {
            setFailure(null);
            setRunId(result.runId);
        },
        onError: (error) => {
            const next = startFailureOf(error);
            // Con el runId de la que ya corre se sigue esa en vez de mostrar error.
            if (next.kind === 'alreadyRunning' && next.runId) {
                setFailure(null);
                setRunId(next.runId);
                return;
            }
            setFailure(next);
        },
    });

    const run = useQuery({
        queryKey: [...KEY, retailerId, target.entity, runId],
        queryFn: () => getRun(retailerId, target.entity, runId ?? ''),
        enabled: runId !== null,
        retry: shouldRetryPoll,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchInterval: (query) =>
            pollIntervalOf(query.state.data?.status, query.state.status === 'error'),
    });

    const open = () => {
        if (inFlight.current) return;
        inFlight.current = true;
        start.mutate();
    };
    const pollFailed = runId !== null && run.isError;

    return {
        start: () => {
            if (runId !== null) return;
            open();
        },
        retry: () => {
            if (pollFailed) {
                void run.refetch();
                return;
            }
            if (run.data?.status === ReconciliationRunStatusEnum.ABORTED) setRunId(null);
            open();
        },
        isStarting: start.isPending,
        failure,
        runId,
        run: runId === null ? undefined : run.data,
        pollFailed,
    };
}
